'use client';

import React from 'react';

interface ImpressumProps {
  tenant: {
    name: string;
    contact_email?: string | null;
    contact_phone?: string | null;
    address?: string | null;
    address_additional?: string | null;
    zip?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    owner_first_name?: string | null;
    owner_last_name?: string | null;
    tax_id?: string | null;
    business_registration_number?: string | null;
    court_of_registry?: string | null;
  };
}

const Impressum: React.FC<ImpressumProps> = ({ tenant }) => {
  const hasAddress = tenant.address && tenant.zip && tenant.city;
  const contactEmail = tenant.contact_email;
  const contactPhone = tenant.contact_phone;

  return (
    <div className="max-h-[60vh] space-y-6 overflow-y-auto text-sm">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Impressum</h3>

        <div>
          <h4 className="mb-2 font-medium text-gray-800">Angaben gemäß § 5 TMG</h4>
          <div className="rounded bg-gray-50 p-3">
            <p className="font-medium">{tenant.name}</p>
            {tenant.owner_first_name && tenant.owner_last_name && (
              <p>
                Inhaber: {tenant.owner_first_name} {tenant.owner_last_name}
              </p>
            )}
            {hasAddress && (
              <>
                <p>{tenant.address}</p>
                {tenant.address_additional && <p>{tenant.address_additional}</p>}
                <p>
                  {tenant.zip} {tenant.city}
                </p>
                {tenant.state && <p>{tenant.state}</p>}
                <p>{tenant.country || 'Deutschland'}</p>
              </>
            )}
          </div>
        </div>

        <div>
          <h4 className="mb-2 font-medium text-gray-800">Kontakt</h4>
          <div className="rounded bg-gray-50 p-3">
            {contactPhone && <p>Telefon: {contactPhone}</p>}
            {contactEmail && <p>E-Mail: {contactEmail}</p>}
          </div>
        </div>

        {(tenant.tax_id || tenant.business_registration_number || tenant.court_of_registry) && (
          <div>
            <h4 className="mb-2 font-medium text-gray-800">Weitere Angaben</h4>
            <div className="rounded bg-gray-50 p-3">
              {tenant.tax_id && <p>Steuernummer: {tenant.tax_id}</p>}
              {tenant.business_registration_number && (
                <p>Handelsregisternummer: {tenant.business_registration_number}</p>
              )}
              {tenant.court_of_registry && <p>Registergericht: {tenant.court_of_registry}</p>}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Haftungsausschluss</h3>

        <div>
          <h4 className="mb-2 font-medium text-gray-800">Haftung für Inhalte</h4>
          <p className="text-gray-600">
            Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten
            nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als
            Diensteanbieter jedoch nicht unter der Verpflichtung, übermittelte oder gespeicherte
            fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine
            rechtswidrige Tätigkeit hinweisen.
          </p>
        </div>

        <div>
          <h4 className="mb-2 font-medium text-gray-800">Haftung für Links</h4>
          <p className="text-gray-600">
            Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen
            Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr
            übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder
            Betreiber der Seiten verantwortlich.
          </p>
        </div>

        <div>
          <h4 className="mb-2 font-medium text-gray-800">Urheberrecht</h4>
          <p className="text-gray-600">
            Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen
            dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art
            der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen
            Zustimmung des jeweiligen Autors bzw. Erstellers.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Streitschlichtung</h3>

        <div>
          <h4 className="mb-2 font-medium text-gray-800">Online-Streitbeilegung (OS)</h4>
          <p className="mb-2 text-gray-600">
            Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:
          </p>
          <p className="text-gray-600">
            <a
              href="https://ec.europa.eu/consumers/odr/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-500"
            >
              https://ec.europa.eu/consumers/odr/
            </a>
          </p>
        </div>

        <div>
          <h4 className="mb-2 font-medium text-gray-800">Verbraucherstreitbeilegung</h4>
          <p className="text-gray-600">
            Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer
            Verbraucherschlichtungsstelle teilzunehmen.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Technische Umsetzung</h3>

        <div>
          <h4 className="mb-2 font-medium text-gray-800">Plattform</h4>
          <p className="text-gray-600">
            Diese Online-Buchungsplattform wird bereitgestellt durch EazyBooking - eine moderne
            Buchungssoftware für Dienstleistungsunternehmen.
          </p>
        </div>

        <div>
          <h4 className="mb-2 font-medium text-gray-800">Hosting</h4>
          <p className="mb-2 text-gray-600">Diese Website wird gehostet von:</p>
          <div className="rounded bg-gray-50 p-3 text-gray-600">
            <p>Vercel Inc.</p>
            <p>340 S Lemon Ave #4133</p>
            <p>Walnut, CA 91789</p>
            <p>United States</p>
          </div>
        </div>
      </div>

      <div className="mt-6 border-t pt-4">
        <p className="text-xs text-gray-500">
          Stand dieses Impressums: {new Date().toLocaleDateString('de-DE')}
        </p>
      </div>
    </div>
  );
};

export default Impressum;
